<template>
    <DefaultLayout>
        <div v-if="!busy" class="container pt-5 w-75">
            <div class="d-flex justify-content-center">
                <div class="w-75">
                    <h2 class="fw-bold">{{ course.name }}</h2>
                </div>
            </div>
            <div class="d-flex justify-content-center">
                <div class="row rounded-3 shadow-lg px-2 py-4 bg-white fs-5 fw-semibold w-75">
                    <div class="col-md-12">
                        <div class="row">
                            <div class="col-md-6 text-center d-flex justify-content-center align-items-center" style="min-height: 280px;">
                                <img v-if="course.thumbnail_url" :src="course.thumbnail_url" width="100%" class="img-fluid rounded-3 h-100" />
                                <i v-else class="fa-solid fa-folder-tree fa-6x"></i>
                            </div>
                            <div class="col-md-6" style="min-height: 280px;">
                                <div class="rounded-3 bg-secondary p-4 h-100 position-relative">
                                    <h4>Welcome!</h4>
                                    <h5 v-if="course.last_item_accessed" style="text-align: left !important;">
                                        <router-link v-if="(course.is_enrolled && ((course.pre_quiz && course.pre_quiz_completed) || !course.pre_quiz)) || course.is_mine" 
                                        :to="{ name: 'view-course', params: { course_slug: course.slug, selected_item_slug: course.last_item_accessed.slug } }"
                                        class="text-secondary text-decoration-none">
                                            Last accessed: "{{ course.last_item_accessed.title.slice(0, 80) }}<span v-if="course.last_item_accessed.title.length > 80" class="ms-n1">...</span>"
                                        </router-link>
                                    </h5>
                                    <div class="fixed-bottom-button">
                                        <button v-if="course.is_enrolled || course.is_mine" class="btn btn-primary mx-1" @click="visitCourse(course)">
                                            <span v-if="course.last_item_accessed">Continue</span>
                                            <span v-else>{{ $t('network.view') }}</span>
                                        </button>
                                        <button v-if="!course.is_enrolled && !course.is_mine" class="btn btn-primary mx-1" @click="enrollInCourse(course)">
                                            Enroll
                                        </button>
                                        <button v-if="course.is_enrolled && !course.is_mine" class="btn btn-outline-primary mx-1" style="border: 2px solid;" @click="unenrollCourse(course)">
                                            Unenroll
                                        </button>
                                        <button v-if="course.is_mine" class="btn btn-outline-primary mx-1" style="border: 2px solid;" @click="editCourse(course)">
                                            {{ $t('edit') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!busy" class="container mt-n5 w-50 text-center">
            <div class="d-flex justify-content-center">
                <div class="row rounded-3 shadow-lg px-2 py-3 mx-3 bg-white fs-5 fw-semibold w-50" style="z-index: 1;">
                    <a class="d-flex text-decoration-none" style="justify-content: space-evenly;">
                        <span v-if="this.$auth.auth" class="d-flex align-items-center">
                            <Fav :loading="loading" :course="true" :value="course.is_liked" @input="toggleLike" />
                        </span>
                        <span class="d-flex align-items-center pointer" @click="showShareModal = !showShareModal">
                            <i class="fas fa-share fa-2x text-primary"></i>&nbsp;
                            <h4 class="text-primary" style="margin: inherit;">{{ $t('share') }}</h4>
                        </span>
                    </a>
                </div>
                <div v-if="course.is_enrolled" class="row rounded-3 shadow-lg px-2 py-3 mx-3 bg-white fs-5 fw-semibold w-50" style="z-index: 1;">
                    <h5>{{ course.completion_percentage }}% complete</h5>
                    <b-progress height="1rem" :max="100" :value="course.completion_percentage" class="px-0"></b-progress>
                </div>
            </div>
        </div>
        <!--Presenter-->
        <div v-if="!busy && course.presenters.length > 0" class="container mt-4">
            <div class="row rounded-3 shadow-lg bg-white px-2 pt-4 pb-3">
                <h4>{{ $t('course-instructor') }} &dash; {{ course.presenters[0].name }}</h4>
                <div v-for="(presenter, index) in course.presenters" :key="index" class="row py-2">
                    <div class="col-9 ps-0 d-flex align-items-center">
                        <img :src="presenter.image" alt="" width="75" class="rounded-circle">
                        <span v-if="presenter.bio" class="ps-4 fs-5 fw-semibold" v-html="presenter.bio"></span>
                        <span v-else class="ps-4 fs-5 fw-semibold">
                            {{ $t('the-instructor-has-not-provided-a-biography') }}
                        </span>
                    </div>
                    <div class="col-3 d-flex align-items-center justify-content-center" style="border-left: 2px solid #5C6D80 !important;">
                        <h5 v-if="presenter.courses.length == 1">1 course published</h5>
                        <h5 v-else>{{presenter.courses}} courses published</h5>
                    </div>
                </div>
            </div>
        </div>
        <!--Description-->
        <div v-if="!busy && course.description" class="container mt-4">
            <div class="row rounded-3 shadow-lg bg-white px-2 pt-4 pb-3">
                <h4>What you'll learn</h4>
                <h5>{{ course.description }}</h5>
            </div>
        </div>
        <!--Modules-->
        <div v-if="!busy && course.modules.length > 0" class="container mt-4">
            <div class="row rounded-3 shadow-lg bg-white pt-2 pb-3">
                <div v-for="module in course.modules" :key="module.id" class="col-12">
                    <div class="card mt-3 p-3" @click="toggleModule(module.id)">
                        <a data-bs-toggle="collapse" :data-bs-target="'#collapseExample' + module.id" :href="'#collapseExample' + module.id" role="button" aria-expanded="false" :aria-controls="'collapseExample' + module.id"
                        style="display: flex; justify-content: space-between; font-weight: 600; text-decoration: none; margin-bottom: 5px;">
                            <h4 class="text-secondary mb-0">{{ module.title }}</h4>
                            <i :class="isModuleOpen(module.id) ? 'fas fa-chevron-up mt-1' : 'fas fa-chevron-down mt-1'"></i>
                        </a>
                        <!--Items-->
                        <div :id="'collapseExample' + module.id" class="form collapse">
                            <h5 v-if="module.description">&bull;&nbsp;{{ module.description }}</h5>
                            <div class="text-secondary">
                                <h5>What's included</h5>
                                <h5>
                                    <i class="fa-solid fa-chalkboard-user"></i>
                                    {{ module.items.length }} lessons
                                    <span v-if="module.duration_in_minutes > 0">
                                        &#124;
                                        <i class="fa-solid fa-clock"></i>&nbsp;{{ module.duration_in_minutes }} minutes
                                    </span>
                                </h5>
                            </div>
                        </div>
                        <div v-for="item in module.items" :id="'collapseExample' + module.id" :key="item.id" class="form collapse"><hr />
                            <div class="mt-2" style="display: flex; justify-content: space-between;">
                                <router-link v-if="(course.is_enrolled && ((course.pre_quiz && course.pre_quiz_completed) || !course.pre_quiz)) || course.is_mine" 
                                :to="{ name: 'view-course', params: { course_slug: course.slug, selected_item_slug: item.slug } }" class="fw-bold text-decoration-none">
                                    <h5>{{ item.title }}</h5>
                                </router-link>
                                <h5 v-else>{{ item.title }}</h5>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--Announcements-->
        <div v-if="!busy" id="course-announcements" class="container mt-4">
            <div class="row rounded-3 shadow-lg bg-white pt-2 pb-3">
                <div class="col-12">
                    <div class="card mt-3 p-3" @click="toggleAnnouncements()">
                        <a data-bs-toggle="collapse" href="#collapseAnnouncements" role="button" 
                           aria-expanded="false" aria-controls="collapseAnnouncements"
                           style="display: flex; justify-content: space-between; font-weight: 600; text-decoration: none; margin-bottom: 5px;">
                            <h4 class="text-secondary mb-0">Announcements</h4>
                            <i :class="isAnnouncementsOpen ? 'fas fa-chevron-up mt-1' : 'fas fa-chevron-down mt-1'"></i>
                        </a>
                        <!-- Announcements overview (always visible) -->
                        <div class="d-flex align-items-center mt-2">
                            <i class="fa-solid fa-megaphone fa-2x"></i>
                            <span class="ps-4 fs-5 fw-semibold">
                                {{ $t('check-out-the-latest-announcements-from-this-course') }}
                            </span>
                        </div>
                        <!-- Collapsible announcements content -->
                        <div v-show="isAnnouncementsOpen" class="mt-3" style="transition: all 0.3s ease;">
                            <div v-if="course.announcements && course.announcements.length > 0">
                                <div v-for="(announcement, index) in course.announcements" :key="announcement.id" class="card mb-3">
                                    <div class="card-header d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0">{{ announcement.title }}</h5>
                                        <div class="d-flex align-items-center">
                                            <span class="text-muted small me-3">{{ formatDate(announcement.created_at) }}</span>
                                            <button class="btn btn-link p-0" 
                                                    :aria-expanded="isAnnouncementOpen(announcement.id)"
                                                    :aria-controls="'announcement-content-' + announcement.id"
                                                    @click.stop="toggleAnnouncement(announcement.id)">
                                                <i :class="isAnnouncementOpen(announcement.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div :id="'announcement-content-' + announcement.id" class="card-body" :class="{ 'pb-0': isAnnouncementOpen(announcement.id) }">
                                        <!-- Preview (always visible) -->
                                        <div v-if="!isAnnouncementOpen(announcement.id)" class="announcement-preview">
                                            <div v-html="getAnnouncementPreview(announcement.content)"></div>
                                            <div v-if="isContentTruncated(announcement.content)" class="text-primary mt-1 pointer" @click.stop="toggleAnnouncement(announcement.id)">
                                                Read more...
                                            </div>
                                        </div>
                                        <!-- Full content (visible when expanded) -->
                                        <div v-if="isAnnouncementOpen(announcement.id)" v-html="announcement.content"></div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="alert alert-info mb-0">
                                No announcements have been posted for this course yet.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="busy" class="text-center" style="padding-top: 11rem;">
            <i class="fas fa-5x fa-spinner"></i>
        </div>

        <b-modal v-model="showShareModal" hide-footer hide-header no-stacking>
            <div class="text-center">
                <span>Share this course to your choice of social media or contacts</span><br /><br />
                <a class="pointer" @click="accessShareLink('Facebook', facebook+home+viewUrl)">
                <svg id="Layer_1" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                width="45" height="45" viewBox="0 0 418.135 418.135" style="enable-background:new 0 0 418.135 418.135; margin-right: 20px;" xml:space="preserve">
                    <path style="fill:#475993;" d="M353.701,0H55.087C24.665,0,0.002,24.662,0.002,55.085v298.616c0,30.423,24.662,55.085,55.085,55.085
                    h147.275l0.251-146.078h-37.951c-4.932,0-8.935-3.988-8.954-8.92l-0.182-47.087c-0.019-4.959,3.996-8.989,8.955-8.989h37.882
                    v-45.498c0-52.8,32.247-81.55,79.348-81.55h38.65c4.945,0,8.955,4.009,8.955,8.955v39.704c0,4.944-4.007,8.952-8.95,8.955
                    l-23.719,0.011c-25.615,0-30.575,12.172-30.575,30.035v39.389h56.285c5.363,0,9.524,4.683,8.892,10.009l-5.581,47.087
                    c-0.534,4.506-4.355,7.901-8.892,7.901h-50.453l-0.251,146.078h87.631c30.422,0,55.084-24.662,55.084-55.084V55.085
                    C408.786,24.662,384.124,0,353.701,0z"/>
                </svg>
                </a>
                <a class="pointer" @click="accessShareLink('WhatsApp', whatsapp+home+viewUrl)">
                <svg id="Layer_1" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                width="45" height="45" viewBox="0 0 418.135 418.135" style="enable-background:new 0 0 418.135 418.135; margin-right: 20px;" xml:space="preserve">
                    <path style="fill:#7AD06D;" d="M198.929,0.242C88.5,5.5,1.356,97.466,1.691,208.02c0.102,33.672,8.231,65.454,22.571,93.536
                    L2.245,408.429c-1.191,5.781,4.023,10.843,9.766,9.483l104.723-24.811c26.905,13.402,57.125,21.143,89.108,21.631
                    c112.869,1.724,206.982-87.897,210.5-200.724C420.113,93.065,320.295-5.538,198.929,0.242z M323.886,322.197
                    c-30.669,30.669-71.446,47.559-114.818,47.559c-25.396,0-49.71-5.698-72.269-16.935l-14.584-7.265l-64.206,15.212l13.515-65.607
                    l-7.185-14.07c-11.711-22.935-17.649-47.736-17.649-73.713c0-43.373,16.89-84.149,47.559-114.819
                    c30.395-30.395,71.837-47.56,114.822-47.56C252.443,45,293.218,61.89,323.887,92.558c30.669,30.669,47.559,71.445,47.56,114.817
                    C371.446,250.361,354.281,291.803,323.886,322.197z"/>
                    <path style="fill:#7AD06D;" d="M309.712,252.351l-40.169-11.534c-5.281-1.516-10.968-0.018-14.816,3.903l-9.823,10.008
                    c-4.142,4.22-10.427,5.576-15.909,3.358c-19.002-7.69-58.974-43.23-69.182-61.007c-2.945-5.128-2.458-11.539,1.158-16.218
                    l8.576-11.095c3.36-4.347,4.069-10.185,1.847-15.21l-16.9-38.223c-4.048-9.155-15.747-11.82-23.39-5.356
                    c-11.211,9.482-24.513,23.891-26.13,39.854c-2.851,28.144,9.219,63.622,54.862,106.222c52.73,49.215,94.956,55.717,122.449,49.057
                    c15.594-3.777,28.056-18.919,35.921-31.317C323.568,266.34,319.334,255.114,309.712,252.351z"/>
                </svg>
                </a>
                <a class="pointer" @click="accessShareLink('LinkedIn', linkedin+home+viewUrl)">
                <svg id="Layer_1" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                width="45" height="45" viewBox="0 0 418.135 418.135" style="enable-background:new 0 0 418.135 418.135; margin-right: 10px;" xml:space="preserve">
                    <path style="fill:#0077B7;" d="M347.445,0H34.555C15.471,0,0,15.471,0,34.555v312.889C0,366.529,15.471,382,34.555,382h312.889
                    C366.529,382,382,366.529,382,347.444V34.555C382,15.471,366.529,0,347.445,0z M118.207,329.844c0,5.554-4.502,10.056-10.056,10.056
                    H65.345c-5.554,0-10.056-4.502-10.056-10.056V150.403c0-5.554,4.502-10.056,10.056-10.056h42.806
                    c5.554,0,10.056,4.502,10.056,10.056V329.844z M86.748,123.432c-22.459,0-40.666-18.207-40.666-40.666S64.289,42.1,86.748,42.1
                    s40.666,18.207,40.666,40.666S109.208,123.432,86.748,123.432z M341.91,330.654c0,5.106-4.14,9.246-9.246,9.246H286.73
                    c-5.106,0-9.246-4.14-9.246-9.246v-84.168c0-12.556,3.683-55.021-32.813-55.021c-28.309,0-34.051,29.066-35.204,42.11v97.079
                    c0,5.106-4.139,9.246-9.246,9.246h-44.426c-5.106,0-9.246-4.14-9.246-9.246V149.593c0-5.106,4.14-9.246,9.246-9.246h44.426
                    c5.106,0,9.246,4.14,9.246,9.246v15.655c10.497-15.753,26.097-27.912,59.312-27.912c73.552,0,73.131,68.716,73.131,106.472
                    L341.91,330.654L341.91,330.654z"/>
                </svg>
                </a>
                <a class="pointer" @click="accessShareLink('Twitter', twitter+home+viewUrl)">
                <svg id="Layer_1" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                width="55" height="55" viewBox="0 0 1668.56 1221.19" style="enable-background:new 0 0 1668.56 1221.19; margin-right: 10px;" xml:space="preserve">
                <g id="layer1" transform="translate(52.390088,-25.058597)">
                    <path id="path1009" d="M283.94,167.31l386.39,516.64L281.5,1104h87.51l340.42-367.76L984.48,1104h297.8L874.15,558.3l361.92-390.99
                    h-87.51l-313.51,338.7l-253.31-338.7H283.94z M412.63,231.77h136.81l604.13,807.76h-136.81L412.63,231.77z"/>
                </g>
                </svg>
                </a>
                <a class="pointer" @click="accessShareLink('Email', email+home+viewUrl)">
                <svg id="Layer_1" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                width="45" height="45" viewBox="0 0 128 128" style="enable-background:new 0 0 128 128;" xml:space="preserve">
                    <circle style="fill:#267CB5;" cx="64" cy="64" r="64"/>
                    <path style="fill:#FFFFFF;" d="M64,28"/>
                    <path style="fill:#FFFFFF;" d="M64,72.4l38.2-32.7c-0.6-0.4-1.4-0.7-2.2-0.7H28c-0.8,0-1.6,0.3-2.2,0.7L64,72.4z"/>
                    <path style="fill:#FFFFFF;" d="M66.6,75.4c-1.5,1.3-3.7,1.3-5.2,0L24,43.5V85c0,2.2,1.8,4,4,4h72c2.2,0,4-1.8,4-4V43.4L66.6,75.4z"/>
                </svg>
                </a>
            </div>
            <div class="form-control align-items-center sharing">
                <span class="url-field">{{ home + '/...' }}</span>
                <div style="float: right;">
                <span class="btn btn-primary btn-sm text-white copy-btn" @click.prevent="copyUrl">
                    <span style="text-transform: capitalize; margin-bottom: -1px;">{{ $t('Invite.copy') }}</span>
                </span>
                </div>
                <input id="url" data-test="url" :value="home+viewUrl" type="hidden">
            </div>
            <span id="copied">{{ $t('Invite.copied') }}</span>
        </b-modal>
    </DefaultLayout>
</template>
<script>
import Fav from '@/components/Fav.vue';
import DefaultLayout from '@/views/layouts/default/';
import moment from 'moment';

export default {
    components: {
        Fav,
        DefaultLayout,
    },
    props: {
        course_slug: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            busy: true,
            loading: false,
            course: null,
            toggleModules: {},
            isAnnouncementsOpen: false,
            openAnnouncements: {},
            home: window.location.origin,
            facebook: 'https://www.facebook.com/share.php?u=',
            whatsapp: 'https://wa.me/?text=',
            linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=',
            twitter: 'https://twitter.com/share?ref_src=twsrc%5Etfw&url=',
            email: 'mailto:?subject=Resonance courses&amp;body=Check out this course ',
            showShareModal: false,
        };
    },
    computed: {
        viewUrl() {
            return '/courses/preview/' + this.course.slug;
        },
    },
    mounted() {
        this.fetchCourse();
        // check if URL has an anchor for announcements
        if (window.location.hash === '#course-announcements') {
            // set announcements section to open as soon as the component is mounted
            this.isAnnouncementsOpen = true;
            // add small delay to ensure DOM is ready and scroll to announcements section
            this.$nextTick(() => {
                setTimeout(() => {
                    document.getElementById('course-announcements').scrollIntoView({ behavior: 'smooth' });
                }, 500);
            });
        }
    },
    methods: {
        fetchCourse() {
            if (!this.$auth.auth) {
                this.$bus.emit('show-sign-in', this.$route.fullPath, 'Please sign in to view this course.');
                return;
            }
            this.$http.get('courses/' + this.course_slug).then(resp => {
                this.course = resp.data;
                // make sure items are sorted by position
                this.course.modules.forEach((module) => {
                    module.items.sort((a, b) => a.position - b.position);
                });
                this.busy = false;
            }).catch(error => {
                if (error && error.status === 404) {
                    this.$router.push('/404');
                } else {
                    console.log('Error fetching course:', error.message);
                    this.busy = false;
                }
            });
        },
        visitCourse(course) {
            if (course.permission === 'authorized' && !this.$auth.auth) {
                this.$bus.emit('show-sign-in', course.url.view, "This course is protected, please sign in to continue.");
            } else {
                this.$router.push({
                    name: 'view-course',
                    params: { course_slug: course.slug },
                });
            }
        },
        enrollInCourse(course) {
            const hasModulesWithItems = course.modules.some(module => module.items && module.items.length > 0);
            if (course.modules.length === 0 || !hasModulesWithItems) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'This course is under construction.',
                    text: 'Please check back later.',
                });
            } else {
                this.$http.post('courses/' + course.slug + '/enroll').then(() => {
                    this.course.is_enrolled = true;
                    this.$router.push({
                        name: 'view-course',
                        params: { course_slug: course.slug },
                    });
                }).catch(error => {
                    console.log('Error enrolling in course:', error.message);
                });
            }
        },
        unenrollCourse(course) {
            this.$swal.fire({
                icon: 'warning',
                title: 'Are you sure you want to unenroll from this course?',
                showCancelButton: true,
                confirmButtonText : this.$t('ResearchCreds.yes'),
                customClass: {
                    confirmButton: 'btn btn-danger mx-1',
                    cancelButton: 'btn btn-primary mx-1',
                },
                buttonsStyling: false,
            }).then( (result) => {
                if(result.isConfirmed){
                    this.$http.post('courses/' + course.slug + '/unenroll').then(() => {
                        this.course.is_enrolled = false;
                    }).catch(error => {
                        console.log('Error unenrolling from course:', error.message);
                    });
                }
            });
        },
        editCourse(course) {
            this.$router.push({
                name: 'update-course',
                params: { course_slug: course.slug },
            });
        },
        toggleModule(moduleId) {
            this.$set(this.toggleModules, moduleId, !this.toggleModules[moduleId]);
        },
        toggleAnnouncements() {
            this.isAnnouncementsOpen = !this.isAnnouncementsOpen;
        },
        toggleAnnouncement(announcementId) {
            this.$set(this.openAnnouncements, announcementId, !this.openAnnouncements[announcementId]);
        },
        isAnnouncementOpen(announcementId) {
            return !!this.openAnnouncements[announcementId];
        },
        getAnnouncementPreview(content) {
            // create a temporary div to parse HTML content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;
            // get text content and truncate it
            let text = tempDiv.textContent || tempDiv.innerText || '';
            if (text.length > 150) {
                return text.substring(0, 150) + '...';
            }
            return text;
        },
        isContentTruncated(content) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;
            let text = tempDiv.textContent || tempDiv.innerText || '';
            return text.length > 150;
        },
        isModuleOpen(moduleId) {
            return !!this.toggleModules[moduleId];
        },
        formatDate(date) {
            return moment.utc(date).local().format("MMM D, YYYY • HH:mm");
        },
        accessShareLink(link_name, link) {
            if (window.umami && typeof window.umami.track === 'function') {
                window.umami.track(`share-${link_name.toLowerCase()}-course-${this.course.id}`);
            }
            let url = 'courses/' + this.course.slug + '/' + this.$store.getters['user']['user_id'] + '/' + link_name;
            this.$http.post(url).then(() => {
                window.location.href = link;
            });
        },
        copyUrl () {
            let urlToCopy = document.querySelector('#url')
            urlToCopy.setAttribute('type', 'text')
            urlToCopy.select()

            document.getElementById("copied").style.display = "inline";
            document.execCommand("copy");
            setTimeout( function() {
                document.getElementById("copied").style.display = "none";
            }, 1000);

            /* unselect the range */
            urlToCopy.setAttribute('type', 'hidden')
            window.getSelection().removeAllRanges()
        },
        toggleLike() {
            this.loading = true;
            this.$http.post('courses/' + this.course.slug + '/toggle-like').then(resp => {
                if (resp.data == 1) {
                    this.course.is_liked = false;
                    // umami tracking for unlike action
                    if (window.umami && typeof window.umami.track === 'function') {
                        window.umami.track(`unlike-course-${this.course.id}`);
                    }
                    this.$swal.fire({
                        text: "Removed from your liked courses",
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                    this.loading = false;
                } else {
                    this.course.is_liked = true;
                    // umami tracking for like action
                    if (window.umami && typeof window.umami.track === 'function') {
                        window.umami.track(`like-course-${this.course.id}`);
                    }
                    this.$swal.fire({
                        text: "Added to your liked courses",
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                    this.loading = false;
                }
            });
        },
    },
};
</script>
<style scoped>
.fixed-bottom-button {
    position: absolute;
    bottom: 45px;
}
.sharing {
  margin-top: 28px;
  background: #D9D9D9;
  padding-right: 9px;
}
.url-field {
  line-height: 24px;
  font-weight: 400;
  text-align: left;
  color: #005F9A;
  font-size: 15px;
  user-select: none;
}
.copy-btn {
  display: flex;
  padding: 5px 20px;
}
.copy-btn span {
  font-size: 10px;
  font-weight: 400;
}
#copied {
  font-size: 14px;
  color: red;
  display: none;
}
.swal2-timer-progress-bar {
  background-color: #198754;
}
</style>